import { useState } from "react";
import {
  HeartHandshake,
  HelpCircle,
  ShieldCheck,
  MessageSquare,
  Sparkles,
  BookOpen,
  Compass,
  Home,
  Coffee,
  ListChecks,
  Receipt,
  Users,
  FilePlus2,
  AlertTriangle,
  CheckCircle2,
  ArrowDown,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  WorkspaceShell,
  WorkspaceSection,
  WorkspaceSupportBlock,
} from "../../components/workspace/WorkspaceShell";

// The JCC-native Care Needs form. Every Free Family (and Family Circle)
// member can post a care need — the existing external carer-request form
// used elsewhere is a separate legacy pathway and is untouched here.
function CareNeedsCard() {
  return (
    <section data-testid="family-hub-care-needs" className="scroll-mt-24">
      <div className="rounded-3xl border border-purple/30 bg-gradient-to-br from-purple/5 via-white to-purple/5 p-7 md:p-9">
        <h2 className="font-display text-xl md:text-2xl font-semibold">Post your care needs</h2>
        <p className="mt-2 text-sm text-ink-muted leading-relaxed max-w-2xl">
          Tell us what care you need. Save a draft privately, send
          it to JCC for review, and manage your post — pause, mark
          matched, or relist — from one place. JCC checks each post
          before sharing it with any carer.
        </p>
        <a href="/family/care-needs"
           data-testid="family-hub-care-needs-cta"
           className="mt-4 inline-flex items-center gap-2 rounded-xl bg-purple px-4 py-2 text-sm font-semibold text-white hover:bg-purple/90">
          Post a care need
        </a>
      </div>
    </section>
  );
}

// Browse approved Pro Carer Profiles — read-only discovery, no contact
// details shown. Available to both Free Family and Family Circle members.
function CarerProfilesCard() {
  return (
    <section data-testid="family-hub-carer-profiles" className="scroll-mt-24">
      <div className="rounded-3xl border border-slate-200 bg-white p-7 md:p-9">
        <h2 className="font-display text-xl md:text-2xl font-semibold">Browse Carer Profiles</h2>
        <p className="mt-2 text-sm text-ink-muted leading-relaxed max-w-2xl">
          Look through approved Pro Carer Profiles from independent carers on Just Care Community. No contact
          details are shown here — post a care need to hear from interested carers directly.
        </p>
        <a href="/family/carer-profiles"
           data-testid="family-hub-carer-profiles-cta"
           className="mt-4 inline-flex items-center gap-2 rounded-xl border-2 border-purple/30 text-purple px-4 py-2 text-sm font-semibold hover:bg-purple/5">
          Browse Carer Profiles
        </a>
      </div>
    </section>
  );
}

/**
 * Family Starter Hub — `/workspace/family-starter-hub`.
 *
 * Calm, professional content hub for free family members. Currently open in
 * preview only (see PreviewOpenRoute). All sections are built modularly so
 * copy can be replaced section-by-section without rebuilding the layout.
 */

const Para = ({ children }) => (
  <p className="text-ink-muted leading-relaxed">{children}</p>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-lg md:text-xl font-semibold text-ink mt-6 first:mt-0">
    {children}
  </h3>
);
const Bullets = ({ items, accent = "purple" }) => {
  const color = accent === "purple" ? "text-purple" : "text-teal";
  return (
    <ul className="mt-3 space-y-2">
      {items.map((it) => (
        <li key={it} className="flex gap-3 text-ink-muted leading-relaxed">
          <CheckCircle2
            className={`w-5 h-5 mt-0.5 shrink-0 ${color}`}
            strokeWidth={1.75}
            aria-hidden="true"
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
};

// ── Smooth scroll helper for anchor buttons ─────────────────────────────────
const scrollToId = (id) => (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ── Start-here cards (anchor-scroll into the guidance accordion) ───────────
const START_HERE = [
  {
    icon: Compass,
    title: "How Independent Care Works",
    body: "Understand what independent care is, how it differs from agency care and what a good care arrangement can look like.",
    cta: "Learn the Basics",
    target: "guide-understanding",
  },
  {
    icon: HelpCircle,
    title: "Finding the Right Carer",
    body: "Guidance to help you think about care needs, personality fit, practical requirements and the questions worth asking.",
    cta: "Explore the Guide",
    target: "guide-misconceptions",
  },
  {
    icon: HeartHandshake,
    title: "Preparing for Care",
    body: "Helpful steps to take before someone starts, so expectations, routines and important information are clearer from day one.",
    cta: "Get Prepared",
    target: "guide-what-it-looks-like",
  },
];

// ── Family Toolkit — clickable cards open a guide accordion below ────────
// "guide" holds the supplied long-form copy:
//   paragraphs: full body, paragraph-by-paragraph
//   basics:     the "Start with the basics:" prompt
//   signpost:   the closing "Paid Family Membership will include…" line
const TOOLKIT = [
  {
    id: "find-a-carer",
    icon: Users,
    title: "How to Find a Carer",
    summary: "Where to look, how to approach first conversations and what to listen for.",
    guide: {
      paragraphs: [
        "Finding the right carer is not simply about finding somebody who is available. It is about finding someone whose experience, personality, communication style and approach to care feel right for the person you love and for your family. That can sound like a lot, especially when care is needed quickly, but you do not need to have every answer before you begin.",
        "Start by thinking about the everyday reality of the care needed. What support does your loved one need now? What is important to them as a person? Do they enjoy company and conversation, prefer a quieter routine, need someone confident with mobility support, dementia care, medication routines or personal care? The more clearly you understand the shape of the role, the easier it becomes to recognise a good fit when you meet one.",
        "It is also worth remembering that qualifications and years of experience matter, but they are not the whole story. A carer may have an excellent background on paper but still not be the right personality match. The best arrangements usually come from a balance of practical capability, kindness, reliability, clear communication and mutual respect.",
        "When you speak to a carer, notice how they respond to your questions. Do they listen carefully? Do they ask thoughtful questions about your loved one rather than jumping straight to price or availability? Are they realistic about what they can and cannot provide? A professional carer should not promise the impossible, and a good family should not feel pressured to make a decision before they are comfortable.",
        "Try not to panic if you do not find the right person immediately. It is far better to take a little time, ask the right questions and have honest conversations than to rush into an arrangement that does not feel right for either side.",
      ],
      basics:
        "Write down the three most important things your loved one needs from a carer, and the three qualities that would help your family feel safe and confident.",
      signpost:
        "Paid Family Membership will include more structured support for families who are ready to take the next step, including care-needs submission, access to carer profiles and practical tools to help you move from searching into a clearer care arrangement.",
    },
  },
  {
    id: "questions-to-ask",
    icon: HelpCircle,
    title: "Questions to Ask Before Choosing a Carer",
    summary: "A thoughtful set of questions that help you find a real fit — not just availability.",
    guide: {
      paragraphs: [
        "Choosing a carer can feel deeply personal, because it is. You are not simply arranging a service. You are inviting someone into your loved one’s day-to-day life, routines, home and private world. The right questions can help you feel more confident, but this does not need to feel like a formal interview or an interrogation.",
        "Begin by asking about the carer’s experience with the type of support your loved one needs. For example, have they supported people living with dementia, reduced mobility, continence needs, Parkinson’s, stroke recovery, anxiety, end-of-life care or social isolation? Ask them what they enjoy about care work and what kind of placements they feel most comfortable with. Their answers often tell you far more than a list of qualifications.",
        "It is also important to talk about practical matters early. Ask about availability, preferred working patterns, breaks, whether they work independently, what they expect from a live-in arrangement and how they like to communicate with families. Clear conversations at the beginning can prevent misunderstandings later.",
        "Do not be afraid to ask how they handle difficult moments. What would they do if your loved one refused care, became upset, had a fall, felt unwell or became confused? You are not looking for a perfect answer. You are looking for someone calm, thoughtful and professional, who understands when to seek help and who does not pretend to know everything.",
        "It is equally important that the carer has the chance to ask questions too. A good arrangement is a two-way conversation. A professional carer will want to understand the person, the home, routines, family involvement and any challenges before agreeing to take on a role.",
        "You may not know exactly what to ask at first, and that is perfectly normal. The aim is not to catch someone out. It is to understand whether the relationship feels respectful, realistic and safe.",
      ],
      basics:
        "Before your first conversation, write down the support your loved one needs each day and one concern you would like the carer to help you understand.",
      signpost:
        "Paid Family Membership will include more detailed conversation prompts and practical decision-making tools to help families compare options and choose with greater confidence.",
    },
  },
  {
    id: "preparing-home",
    icon: Home,
    title: "Preparing Your Home for a Live-In Carer",
    summary: "Simple, sensible touches that make the arrangement comfortable from day one.",
    guide: {
      paragraphs: [
        "Preparing your home for a live-in carer does not mean turning it into a care facility. It means creating a practical, respectful environment where your loved one can remain comfortable in familiar surroundings and the carer has what they need to do their job well.",
        "The most important starting point is privacy. A live-in carer needs their own suitable sleeping space, somewhere they can keep personal belongings and enough privacy to rest properly during their agreed break time. This is not a luxury. Rest is essential for anyone providing care, particularly in a live-in role where they are sharing the home and supporting someone day after day.",
        "Think about the everyday practicalities too. Is there reliable Wi-Fi and mobile signal? Is the bathroom safe and accessible? Are medications, care notes, emergency contacts and key household information easy to find? Are there any trip hazards, difficult stairs, heavy doors, pets, alarms, parking restrictions or access issues that a carer should know about in advance?",
        "Food and household arrangements are another area that is easier to discuss before someone begins. Carers are human beings, not guests who should be expected to guess what is available to them. Agreeing simple expectations around meals, kitchen use, shopping, laundry, heating and household routines can make the arrangement feel more comfortable for everyone.",
        "It can also help to think about your loved one’s usual rhythm. What time do they like to get up? What do they enjoy watching, eating or doing? Which routines matter to them? The more a carer understands the person behind the care needs, the easier it is to help them feel settled and respected.",
        "You do not need to make everything perfect before a carer starts. The aim is simply to be honest about the home, practical about what is needed and willing to make reasonable adjustments where necessary.",
      ],
      basics:
        "Walk through the home as though you were arriving for the first time. Make a note of anything a new carer would need to know on day one.",
      signpost:
        "Paid Family Membership will include more detailed preparation guidance and practical tools to help families organise the home and key information before care begins.",
    },
  },
  {
    id: "breaks-rest-cover",
    icon: Coffee,
    title: "Understanding Breaks, Rest and Cover",
    summary: "What's reasonable for the carer, what's reasonable for the family, and how to plan ahead.",
    guide: {
      paragraphs: [
        "One of the biggest misunderstandings around live-in care is the idea that one person can provide constant hands-on support every hour of every day. A live-in carer may be present in the home, but that does not mean they are working without rest, without privacy or without time away from their responsibilities.",
        "Breaks are not a favour to the carer. They are part of a safe, sustainable and professional care arrangement. A carer who is properly rested is more able to be patient, alert, kind and consistent. When someone is expected to be “on duty” all day and all night without clear boundaries, the arrangement can quickly become unfair, exhausting and unsafe for everyone involved.",
        "Families often worry because they genuinely need their loved one to be safe. That is completely understandable. The key is to think realistically about what support is needed during the carer’s agreed break and overnight. Sometimes a family member can cover for a short period. In other situations, a second carer, sitter, neighbour, visiting support worker or planned respite arrangement may be needed. The right option depends on the person’s needs and the level of risk.",
        "It helps to discuss breaks before the arrangement begins rather than waiting until someone is tired or frustrated. Talk openly about what time the carer will usually have, what happens if an urgent situation arises and whether there are particular days when family support is available. Clear expectations reduce stress on both sides.",
        "It is also important to understand that “live-in care” does not automatically mean “24-hour care.” If someone needs frequent overnight intervention, continual supervision or two people for certain tasks, the care plan may need more than one individual. Being realistic about this early protects your loved one and prevents a carer from being placed in an impossible position.",
      ],
      basics:
        "Think about when your loved one needs the most support in a typical day, and whether there is anyone who could safely provide cover during an agreed break.",
      signpost:
        "Paid Family Membership will include more detailed guidance to help families plan care arrangements, breaks, cover and expectations in a fair and practical way.",
    },
  },
  {
    id: "communication-boundaries",
    icon: MessageSquare,
    title: "Clear Communication and Boundaries",
    summary: "How to set respectful expectations early so small things stay small.",
    guide: {
      paragraphs: [
        "Most care arrangements do not break down because people are unkind or uncaring. They become difficult when important things are left unsaid, assumptions are made or people are afraid to raise concerns until they have become much bigger than they needed to be.",
        "Clear communication is one of the kindest things a family and carer can offer each other. It helps everyone understand what is expected, what is changing and where support may be needed. This includes practical matters such as routines, appointments, food, medication information, household tasks, visitors, breaks, expenses and communication with family members. It also includes the quieter things: how your loved one likes to be spoken to, what comforts them, what upsets them and what helps them feel safe.",
        "Boundaries matter too. A carer may become very close to the person they support, but they are still a professional with agreed responsibilities, working hours, rest time and personal privacy. Families deserve reassurance and good communication, but carers should not feel they must be available to answer messages constantly or manage family disagreements. Respectful boundaries make it easier for everyone to work together well.",
        "It can be helpful to agree early on how updates will be shared. Perhaps there is one main family contact, a group message for important updates or a short weekly check-in. This can stop the carer receiving multiple messages from different relatives with conflicting requests.",
        "When concerns arise, try to deal with them calmly and directly. Ask questions rather than assuming the worst. Give the carer the opportunity to explain, and be willing to listen to their professional observations too. The strongest arrangements are built on mutual respect, not control.",
      ],
      basics:
        "Agree who the main family contact will be and how you would like important updates to be shared.",
      signpost:
        "Paid Family Membership will include practical prompts and structured tools to help families and carers set expectations early and keep communication clearer as care develops.",
    },
  },
  {
    id: "emergency-info",
    icon: ShieldCheck,
    title: "Basic Emergency Information Sheet",
    summary: "Keep the essentials in one easy-to-find place — contacts, medical, key information.",
    guide: {
      paragraphs: [
        "In an emergency, people do not need more information. They need the right information quickly. A simple emergency information sheet can make a real difference when a carer, family member, neighbour or emergency professional needs to understand important details without searching through drawers, phones or messages.",
        "The sheet should bring together the essentials in one easy-to-find place. This may include your loved one’s full name, date of birth, address, NHS number if known, GP details, next of kin, key medical conditions, allergies, medication information, mobility needs, communication needs and anything emergency services may need to know straight away.",
        "It can also include practical information that is easy to forget in a stressful moment: where medicines are kept, whether there is a key safe, whether there are pets in the home, how to access the property, who should be contacted first and whether there are advance care plans or important documents that family members should know about.",
        "This does not need to be complicated or overly medical. The purpose is to create a calm, reliable snapshot of the most important information. It should be kept somewhere sensible and agreed with the carer, while still respecting privacy and confidentiality.",
        "Remember to review it when circumstances change. A new medication, a different GP, a new emergency contact or a change in mobility can make old information less useful. A simple check every few months can help ensure it still reflects the person’s current needs.",
      ],
      basics:
        "Write down the name and number of your loved one’s GP, the main family contact and any key medical information that would be needed in an emergency.",
      signpost:
        "Paid Family Membership will include an editable Basic Emergency Information Sheet so families and carers can keep this information clear, organised and easy to update.",
    },
  },
  {
    id: "care-agreement",
    icon: FilePlus2,
    title: "Care Agreement Guidance",
    summary: "What to put in writing once, so everyone has the same understanding.",
    guide: {
      paragraphs: [
        "A care agreement is not about making an arrangement feel formal or unfriendly. It is about helping everyone understand what has been agreed, so there is less room for confusion later. When a family and carer are both clear about the role, responsibilities, expectations and practical arrangements, it creates a stronger foundation for trust.",
        "A good agreement should reflect the real arrangement rather than a vague idea of “general care.” It should make clear what support is expected, when the carer is available, what breaks are agreed, what tasks are included, what is outside the role and how changes will be discussed. It should also cover practical matters such as payment, expenses, notice periods, holiday arrangements, emergency procedures and how concerns can be raised.",
        "This is especially important in independent care arrangements, where there may not be an agency sitting in the middle. Direct care can be more personal, more flexible and often better value, but it works best when both sides understand where they stand. A written agreement gives everyone something to return to if memories differ or circumstances change.",
        "It is not about expecting problems. It is about preventing small misunderstandings from becoming bigger ones. For example, a family may assume household cleaning is included, while the carer believes their role is focused on personal support. A family may assume a carer will be available overnight, while the carer has agreed to a daytime role with emergency-only support. These things are much easier to clarify before work begins.",
        "The agreement should never replace kindness, communication or common sense. It simply supports them. It gives the carer confidence that they know what is expected and gives the family reassurance that important areas have been thought through properly.",
      ],
      basics:
        "Write down the main support your loved one needs and one practical expectation you would want to make clear before a carer starts.",
      signpost:
        "Paid Family Membership will include more detailed care-agreement guidance and practical tools to help families and carers create a clearer, more professional arrangement together.",
    },
  },
  {
    id: "payment-cancellation",
    icon: Receipt,
    title: "Payment and Cancellation Guidance",
    summary: "Plain guidance on fair pay, notice periods and what to agree up front.",
    guide: {
      paragraphs: [
        "Talking about money can feel awkward, particularly when care is involved. Families may feel uncomfortable raising it because they are focused on their loved one’s needs, while carers may worry about appearing difficult or transactional. In reality, clear payment and cancellation arrangements are part of treating each other fairly and professionally.",
        "Before care begins, it helps to agree the basic financial picture. This includes the carer’s rate, how and when they will be paid, whether payment is weekly or monthly, how expenses will be handled and what costs are included or excluded. For example, travel, food, mileage, parking, accommodation arrangements or additional hours may need to be discussed separately.",
        "It is also important to understand what happens if plans change. Care needs can alter quickly. A hospital admission, family emergency, change in circumstances or sudden decision to end care can affect both the family and the carer. A fair cancellation or notice arrangement gives both sides some protection and helps avoid difficult conversations at an already stressful time.",
        "A clear agreement does not mean there can never be flexibility. It simply means that flexibility is discussed rather than assumed. Families should feel able to ask questions if they are unsure what they are paying for, and carers should feel able to raise concerns if payment is late, duties change or the arrangement becomes different from what was originally agreed.",
        "Independent care can often offer better value for families and fairer pay for carers, but only when the financial side is open, realistic and agreed upfront. Nobody should feel embarrassed about wanting clarity. It protects the relationship and allows everyone to focus more fully on the person receiving care.",
      ],
      basics:
        "Before agreeing to care, make a short list of what you need to understand about payment, expenses, notice and what happens if care is cancelled or changes suddenly.",
      signpost:
        "Paid Family Membership will include practical payment and cancellation guidance, along with tools to help families and carers agree these important details clearly from the beginning.",
    },
  },
];

// ── 3-step "When You Find the Right Carer" journey ─────────────────────────
const STEPS = [
  {
    n: 1,
    title: "Discuss the Arrangement",
    body: "Talk through care needs, routines, expectations, availability and practical details.",
  },
  {
    n: 2,
    title: "Agree the Important Things",
    body: "Use clear guidance and templates to help make sure both sides understand the arrangement.",
  },
  {
    n: 3,
    title: "Create Your Care Partnership Space",
    body: "Paid Family Members can access the next stage of support, including submitting care needs, viewing carer profiles and entering a private shared Care Partnership Room once they have found their carer.",
  },
];

export default function FamilyStarterHub() {
  // Local 'expanded' state for the supplied Free Family Resources copy
  // (kept as a single section so you can paste replacements easily later).
  const [showWelcomeText, setShowWelcomeText] = useState(false);

  // Controlled state for the toolkit guide accordion. Clicking a toolkit
  // card opens the matching item AND smooth-scrolls to it.
  const [openGuide, setOpenGuide] = useState("");
  const openToolkitGuide = (id) => {
    setOpenGuide((prev) => (prev === id ? "" : id));
    // Defer the scroll until the accordion has had a chance to expand.
    setTimeout(() => {
      const el = document.getElementById(`toolkit-guide-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <WorkspaceShell
      testId="hub-family-starter"
      eyebrow="Family Starter Hub"
      memberBadge="Free Family member"
      accent="purple"
      title="Welcome to Your Family Care Hub"
      intro="A calm, practical place to help you understand independent care, make confident choices and create a clearer partnership with the right carer."
    >
      {/* Reassurance panel directly under the hero */}
      <section
        data-testid="family-hub-reassurance"
        className="rounded-3xl border border-purple/15 bg-purple/5 p-6 md:p-8 -mt-2"
      >
        <p className="text-base md:text-lg text-ink leading-relaxed max-w-3xl">
          You do not need to figure everything out at once. Start with the
          information that feels most useful today, and return whenever you
          need to.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href="#start-here" onClick={scrollToId("start-here")} className="btn-purple !py-2.5 !px-5 text-sm" data-testid="reassurance-cta-start">
            Where should I begin?
            <ArrowDown className="w-4 h-4" strokeWidth={2} />
          </a>
          <button
            type="button"
            onClick={() => setShowWelcomeText((v) => !v)}
            className="btn-outline !py-2.5 !px-5 text-sm"
            data-testid="reassurance-cta-welcome-toggle"
          >
            {showWelcomeText ? "Hide welcome message" : "Read a calmer welcome"}
            <ChevronRight
              className={`w-4 h-4 transition-transform ${showWelcomeText ? "rotate-90" : ""}`}
              strokeWidth={2}
            />
          </button>
        </div>

        {showWelcomeText && (
          <div className="mt-6 grid md:grid-cols-2 gap-6 md:gap-8 pt-6 border-t border-purple/15" data-testid="welcome-text-panel">
            <div>
              <H3>Welcome to Your Just Care Community Family Free Site</H3>
              <Para>
                This is your personal space to learn about independent care at your own pace, without pressure or
                obligation. You don’t need to make decisions today. You’re here to understand your options — and that’s
                enough.
              </Para>
              <H3>Your membership level</H3>
              <Para><strong>Current plan: Free Client / Family.</strong> You can upgrade at any time if and when it suits you.</Para>
            </div>
            <div>
              <H3>A calmer way to think about care</H3>
              <Para>Independent care isn’t about rushing decisions or cutting corners. It’s about:</Para>
              <Bullets
                accent="purple"
                items={[
                  "Building trust",
                  "Creating consistency",
                  "Communicating clearly",
                  "Respecting everyone involved",
                ]}
              />
              <Para>
                Many families choose to spend time learning here before arranging care — especially when planning ahead.
                You are welcome to do the same.
              </Para>
              <H3>You’re not behind</H3>
              <Para>
                Care decisions are rarely simple, and needing time to understand your options is completely normal. You
                can return to this hub whenever you need to — whether that’s tomorrow, next month, or further down the
                line. We’re here to support clarity, not urgency.
              </Para>
            </div>
          </div>
        )}
      </section>

      {/* ── 2. Start Here ───────────────────────────────────────────────── */}
      <section id="start-here" data-testid="family-hub-start-here" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <div className="overline text-purple mb-2">Start here</div>
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Start Here: Understanding Independent Care
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Three short reads to help you get your bearings. Take them in any order — they all live on this page.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {START_HERE.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.title}
                className="card-base p-6 flex flex-col"
                data-testid={`start-here-${c.target}`}
              >
                <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug">{c.title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">{c.body}</p>
                <a
                  href={`#${c.target}`}
                  onClick={scrollToId(c.target)}
                  className="mt-5 inline-flex items-center justify-between text-sm font-semibold text-purple pt-4 border-t border-slate-100"
                  data-testid={`start-here-cta-${c.target}`}
                >
                  <span>{c.cta}</span>
                  <ChevronRight className="w-4 h-4" strokeWidth={2} />
                </a>
              </article>
            );
          })}
        </div>
      </section>

      {/* ── Guidance accordion (the long-form supplied copy) ───────────── */}
      <section id="guidance" data-testid="family-hub-guidance" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Family guidance &amp; reading
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Tap a section to read. Take it at your own pace — there’s no rush.
          </p>
        </div>

        <Accordion type="multiple" className="space-y-3" data-testid="family-hub-guidance-accordion">
          {/* Understanding Independent Care */}
          <AccordionItem value="guide-understanding" id="guide-understanding" className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24">
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <Compass className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">Understanding Independent Care</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <Para>
                Independent care means arranging care directly with a self-employed carer, rather than through a care
                agency. The carer works for you (or your family member), not for a third-party organisation. This allows
                care to be built around the individual — their routines, preferences and needs — rather than agency
                availability or shift structures.
              </Para>
              <Para>This allows families to:</Para>
              <Bullets
                accent="purple"
                items={[
                  "Build consistent, one-to-one care relationships",
                  "Communicate directly with the person providing care",
                  "Have greater flexibility around routines and needs",
                  "Avoid high agency fees",
                ]}
              />
              <Para>
                Independent carers are trained, experienced professionals. Many have worked in care for years through
                agencies before choosing to work independently so they can offer more consistent, person-centred care.
              </Para>
              <Para>Independent care is:</Para>
              <Bullets
                accent="purple"
                items={[
                  "Legal and widely used",
                  "Common for live-in, respite, and long-term care",
                  "Often more flexible and personal",
                ]}
              />
              <Para>
                What makes independent care work well is clarity — understanding roles, responsibilities and expectations
                from the start. Independent care isn’t about “doing everything yourself”. It’s about removing unnecessary
                middle layers while still working professionally and ethically.
              </Para>
              <Para>
                Clear written agreements help prevent misunderstandings, protect working relationships and create
                stability in care. Prior agreements keep everyone safe and avoid confusion. These documents are designed
                to support respectful, transparent, independent care arrangements.
              </Para>
            </AccordionContent>
          </AccordionItem>

          {/* Agency vs Independent */}
          <AccordionItem value="guide-vs-agency" id="guide-vs-agency" className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24">
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <ListChecks className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">How Independent Care Differs from Agency Care</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <Para>
                At a glance, agency care and independent care can look similar — a carer arrives and provides support.
                But behind the scenes, the structures are very different.
              </Para>

              <H3>With agency care</H3>
              <Bullets
                accent="purple"
                items={[
                  "The agency employs or contracts the carer",
                  "Families pay the agency, not the carer directly",
                  "A large portion of the fee goes to the agency",
                  "Carers may change due to rotas or availability",
                  "Communication often goes through a third party",
                ]}
              />
              <Para>
                Agencies can be useful in urgent or short-term situations, but they often come with high costs, limited
                flexibility and less consistency in carers.
              </Para>

              <H3>With independent care</H3>
              <Bullets
                accent="purple"
                items={[
                  "Families work directly with the carer",
                  "Pay is agreed transparently",
                  "The carer keeps 100% of their fee",
                  "Care is usually more consistent",
                  "Communication is clearer and more personal",
                ]}
              />
              <Para>Many families choose independent care because it allows:</Para>
              <Bullets
                accent="purple"
                items={[
                  "Stronger relationships",
                  "Greater continuity",
                  "More control over routines",
                  "Better value for money",
                ]}
              />
              <Para>
                Neither option is “right” for everyone — but they are not interchangeable, and understanding the
                difference helps families make informed choices.
              </Para>
            </AccordionContent>
          </AccordionItem>

          {/* Pay and Responsibilities */}
          <AccordionItem value="guide-pay" id="guide-pay" className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24">
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <Receipt className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">Understanding Pay and Responsibilities</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <Para>
                One of the most important parts of independent care is understanding who is responsible for payment and
                decisions. In independent care arrangements:
              </Para>
              <Bullets
                accent="purple"
                items={[
                  "The carer is paid directly",
                  "Payment terms should be agreed in advance",
                  "Responsibilities should be clear from the start",
                ]}
              />
              <Para>Families should understand:</Para>
              <Bullets
                accent="purple"
                items={[
                  "Who will be responsible for paying the carer",
                  "How often payment is made",
                  "What happens if circumstances change",
                  "What is agreed if funding is delayed or pending",
                ]}
              />
              <Para>
                A common source of stress in care arrangements is assumed funding — for example, expecting a council or
                third party to pay without formal confirmation. In many cases, families remain responsible for payment
                even when funding is anticipated.
              </Para>
              <Para>Clear agreements around pay reduce misunderstandings, support respectful working relationships, and protect both the family and the carer.</Para>
              <Para>
                Talking openly about money can feel uncomfortable, but clarity prevents far greater stress later.
                Independent care works best when care remains human and payment remains clear.
              </Para>
            </AccordionContent>
          </AccordionItem>

          {/* Misconceptions */}
          <AccordionItem value="guide-misconceptions" id="guide-misconceptions" className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24">
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <HelpCircle className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">Common Misconceptions About Independent Care</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <H3>“Independent care isn’t safe”</H3>
              <Para>
                Safety comes from communication, boundaries and professionalism — not from whether an agency sits in the
                middle. Many independent carers work to extremely high standards and provide more consistent care than
                agency rotations allow.
              </Para>
              <H3>“Only wealthy families use independent carers”</H3>
              <Para>
                In reality, many families pay less overall for independent care, while carers are paid more fairly.
                Agency fees significantly inflate costs.
              </Para>
              <H3>“It’s complicated to manage”</H3>
              <Para>
                Independent care can actually be simpler. Clear agreements and direct communication often reduce
                confusion compared to agency-managed arrangements.
              </Para>
              <H3>“Families are left alone”</H3>
              <Para>
                Independent care doesn’t mean unsupported care. Platforms like Just Care Community exist to provide
                guidance, structure and clarity — without taking control away from families.
              </Para>
              <H3>“You have to decide everything immediately”</H3>
              <Para>
                You don’t. Many families spend time learning, planning and preparing before arranging care. Taking time
                is not a failure — it’s responsible.
              </Para>
            </AccordionContent>
          </AccordionItem>

          {/* What it really looks like */}
          <AccordionItem value="guide-what-it-looks-like" id="guide-what-it-looks-like" className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24">
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <BookOpen className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">Independent Care: What It Really Looks Like</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <Para>
                Choosing an independent carer does not mean taking unnecessary risks or trying to manage everything alone.
                With the right preparation, clear boundaries and a professional framework, independent care can give
                families more choice, continuity and control — while allowing carers to be paid fairly for the work they do.
              </Para>

              <H3>“Independent care isn’t safe”</H3>
              <Para>
                Safety does not come from an agency sitting in the middle. It comes from choosing carefully, asking the
                right questions, using clear written agreements and keeping communication open. Many independent carers
                are highly experienced professionals who choose self-employment because it gives them more control over
                the care they provide. They may also offer more consistency than agency care, where multiple carers can
                rotate through a package. JCC helps families understand the practical steps involved, including what to
                ask, what to agree and what records are useful to keep.
              </Para>

              <H3>“Only wealthy families use independent carers”</H3>
              <Para>
                Independent care is not only for wealthy families. Agency fees can add a substantial amount to the overall
                cost of care, while the carer themselves may receive only a proportion of what the family pays. Working
                directly with a self-employed carer can often mean the carer is paid more fairly while the family has a
                clearer understanding of where their money is going. Every care arrangement is different, but independent
                care can be a more transparent and flexible option.
              </Para>

              <H3>“It’s too complicated to manage”</H3>
              <Para>
                It can feel overwhelming at first, especially when you are already worried about someone you love. But
                independent care does not have to mean chaos. A clear agreement, agreed hours, payment terms, emergency
                information and regular communication can make the arrangement far simpler than families expect. JCC is
                designed to help you put those foundations in place step by step, rather than trying to make every
                decision at once.
              </Para>

              <H3>“Families are left alone”</H3>
              <Para>You are not expected to become an expert overnight. The Free Family Membership helps you understand the basics of finding, choosing and working with an independent carer. When you are ready for more support, the full JCC system gives you a clearer route from first enquiry to an established care partnership.</Para>
              <Para>You can move through the process at your own pace:</Para>

              <H3>Learn and prepare</H3>
              <Para>
                Understand the independent care route, what to consider, what questions to ask and how to prepare before
                speaking to carers.
              </Para>

              <H3>Find and speak with potential carers</H3>
              <Para>
                You may find carers through recommendations, care platforms, local networks or job advertising. JCC does
                not act as an agency or take commission for introductions.
              </Para>

              <H3>Use a Care Discussion Room</H3>
              <Para>
                Once you have identified a potential carer, you can invite them into a private JCC Care Discussion Room.
                This gives both of you a calm, structured place to discuss the role, expectations, availability, pay,
                accommodation and practical arrangements.
              </Para>

              <H3>Agree the important details before committing</H3>
              <Para>
                You can use JCC guidance and templates to help make sure key points are discussed properly — including
                duties, breaks, rates, food arrangements, time off, notice periods, emergency contacts and boundaries.
              </Para>

              <H3>Enter your Care Partnership Room</H3>
              <Para>
                When you and your carer agree to work together, you can move into your shared Care Partnership Room.
                This becomes your private workspace for the practical side of the arrangement, including agreed
                documents, important information, records and ongoing communication.
              </Para>

              <H3>Keep the arrangement clear as care evolves</H3>
              <Para>
                Care needs can change. A shared space makes it easier to review what is working, raise concerns early and
                keep important information organised for both the family and carer.
              </Para>

              <H3>“You have to decide everything immediately”</H3>
              <Para>
                You do not. Finding the right care arrangement is a big decision, and taking time to understand your
                options is responsible — not a failure. You can begin with the free resources, learn what good
                independent care looks like and prepare before you start looking. When the time feels right, JCC is here
                to help you move from uncertainty to a clearer, safer and more professional care partnership.
              </Para>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* ── 2b. Care Needs card (JCC-native posting form) ─── */}
      <CareNeedsCard />

      {/* ── 2c. Browse Carer Profiles ─── */}
      <CarerProfilesCard />

      {/* ── 3. Your Family Toolkit ─────────────────────────────────────── */}
      <section id="toolkit" data-testid="family-hub-toolkit" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Your Family Toolkit
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Simple guidance and practical resources to help you feel more organised, informed and supported. Tap a
            card to open the full guide below.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOOLKIT.map((t) => {
            const Icon = t.icon;
            const isOpen = openGuide === t.id;
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => openToolkitGuide(t.id)}
                aria-expanded={isOpen}
                aria-controls={`toolkit-guide-${t.id}`}
                data-testid={`toolkit-card-${t.id}`}
                className={`text-left card-base p-6 flex flex-col group transition-shadow focus:outline-none focus:ring-2 focus:ring-purple/40 focus:ring-offset-2 hover:shadow-md ${
                  isOpen ? "ring-2 ring-purple/40" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <span className="pill bg-purple/10 text-purple">Start with the basics</span>
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug">{t.title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">{t.summary}</p>
                <span className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-sm font-semibold text-purple">
                  {isOpen ? "Reading guide ↓" : "Read guide"}
                  <ChevronRight
                    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : "group-hover:translate-x-0.5"}`}
                    strokeWidth={2}
                  />
                </span>
              </button>
            );
          })}
        </div>

        {/* Controlled accordion sitting beneath the toolkit grid. Clicking a
            card sets the open value AND smooth-scrolls to the matching item.
            type="single" + collapsible keeps the page calm and uncluttered. */}
        <Accordion
          type="single"
          collapsible
          value={openGuide}
          onValueChange={(v) => setOpenGuide(v)}
          className="mt-7 space-y-3"
          data-testid="toolkit-guides-accordion"
        >
          {TOOLKIT.map((t) => {
            const Icon = t.icon;
            return (
              <AccordionItem
                key={t.id}
                value={t.id}
                id={`toolkit-guide-${t.id}`}
                className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24"
              >
                <AccordionTrigger className="py-5 hover:no-underline">
                  <span className="flex items-center gap-3 text-left">
                    <Icon className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                    <span className="font-display text-lg font-semibold">{t.title}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pt-1 text-base leading-relaxed">
                  {t.guide.paragraphs.map((p, i) => (
                    <p key={i} className="text-ink-muted leading-relaxed mt-4 first:mt-0">
                      {p}
                    </p>
                  ))}

                  {/* Start with the basics — highlighted callout */}
                  <div
                    className="mt-7 rounded-2xl border border-purple/20 bg-purple/5 p-5 md:p-6"
                    data-testid={`toolkit-basics-${t.id}`}
                  >
                    <div className="overline text-purple mb-2">Start with the basics</div>
                    <p className="text-ink leading-relaxed">{t.guide.basics}</p>
                  </div>

                  {/* Calm Paid Family Membership signpost */}
                  <div
                    className="mt-5 rounded-2xl bg-[#f7f7f3] border border-slate-200 p-5 md:p-6"
                    data-testid={`toolkit-signpost-${t.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-purple shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
                      <p className="text-sm md:text-base text-ink-muted leading-relaxed">{t.guide.signpost}</p>
                    </div>
                  </div>

                  <div className="mt-5 flex">
                    <button
                      type="button"
                      onClick={() => setOpenGuide("")}
                      className="text-sm font-semibold text-purple inline-flex items-center gap-1.5 hover:underline"
                      data-testid={`toolkit-close-${t.id}`}
                    >
                      <ChevronRight className="w-4 h-4 -rotate-90" strokeWidth={2} />
                      Close guide
                    </button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>

      {/* ── 4. What Happens When You Find a Carer ──────────────────────── */}
      <section id="when-you-find" data-testid="family-hub-when-you-find" className="scroll-mt-24">
        <div className="rounded-3xl bg-white border border-slate-200 p-7 md:p-10">
          <div className="overline text-purple mb-3">Your journey</div>
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight max-w-2xl">
            When You Find the Right Carer
          </h2>
          <p className="mt-3 text-ink-muted leading-relaxed max-w-3xl">
            Once you have found someone you would like to work with, Just Care Community can help you move from
            conversations into a clearer, more organised care partnership.
          </p>

          <ol className="mt-8 grid md:grid-cols-3 gap-5" data-testid="family-hub-journey-steps">
            {STEPS.map((s) => (
              <li key={s.n} className="rounded-2xl border border-purple/15 bg-purple/5 p-5 md:p-6">
                <div className="w-9 h-9 rounded-full bg-purple text-white flex items-center justify-center font-semibold text-sm">
                  {s.n}
                </div>
                <h3 className="font-display text-base md:text-lg font-semibold mt-4">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href="#ready-for-more"
              onClick={scrollToId("ready-for-more")}
              className="btn-purple !py-2.5 !px-5 text-sm"
              data-testid="explore-family-membership"
            >
              Explore Family Membership Options
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </a>
            <span className="text-xs text-ink-soft">Paid Family Membership is coming soon.</span>
          </div>
        </div>
      </section>

      {/* ── 5. You Are Not Doing This Alone ────────────────────────────── */}
      <section data-testid="family-hub-not-alone" className="scroll-mt-24">
        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-2">
            <div className="overline text-purple mb-2">Reassurance</div>
            <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
              You Are Not Doing This Alone
            </h2>
            <p className="mt-3 text-ink-muted leading-relaxed">
              Independent care does not mean unsupported care. The right information, clear agreements and respectful
              communication can make a huge difference for families and carers alike.
            </p>
          </div>
          <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
            {[
              { icon: HeartHandshake, body: "Clearer expectations from the beginning" },
              { icon: MessageSquare, body: "More direct communication with the person providing care" },
              { icon: ListChecks, body: "Practical tools to help keep important information organised" },
            ].map((p, i) => {
              const I = p.icon;
              return (
                <div key={i} className="card-base p-5 flex flex-col" data-testid={`reassurance-point-${i + 1}`}>
                  <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                    <I className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <p className="mt-3 text-sm text-ink leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 6. Gentle upgrade panel + What Paid Membership Includes ────── */}
      <section id="ready-for-more" data-testid="family-hub-ready-for-more" className="scroll-mt-24">
        <div className="relative overflow-hidden rounded-3xl border border-purple/15 bg-gradient-to-br from-purple/5 via-white to-purple/5 p-7 md:p-10">
          <div className="absolute left-0 top-0 h-full w-1.5 bg-purple" aria-hidden="true" />
          <div className="overline text-purple mb-3">When you’re ready</div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-2xl">
            Ready for More Support?
          </h2>
          <p className="mt-3 text-ink-muted leading-relaxed max-w-3xl">
            Paid Family Membership is designed for families who are ready to take the next step. It will give you
            access to tools for submitting care needs, viewing suitable carer profiles and, once you have found your
            carer, entering your private Care Partnership Room together.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a href="/membership/family-circle" className="btn-purple" data-testid="family-starter-upgrade-cta">
              Explore Family Care Office — £19/month
            </a>
          </div>

          {/* Expandable: full "What Paid Family Membership Includes" copy */}
          <Accordion type="single" collapsible className="mt-7" data-testid="paid-membership-details">
            <AccordionItem
              value="paid-includes"
              className="rounded-2xl border border-purple/15 bg-white px-5 md:px-7"
            >
              <AccordionTrigger className="py-5 hover:no-underline">
                <span className="flex items-center gap-3 text-left">
                  <Sparkles className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                  <span className="font-display text-lg font-semibold">
                    What Paid Family Membership will include
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
                <Para>
                  Upgrading to Just Care Community Family Care Office membership is designed for families who are ready to
                  move beyond learning and want practical support in arranging care properly. It provides structure,
                  clarity and peace of mind — without agency pressure.
                </Para>

                <H3>Practical tools to arrange care confidently</H3>
                <Para>Paid Family Members gain access to:</Para>
                <Bullets
                  accent="purple"
                  items={[
                    "Clear booking and care arrangement templates",
                    "Guidance on setting expectations and routines",
                    "Support understanding roles and responsibilities",
                    "Access to Carer Profiles",
                    "Private JCC Care Partnership Room",
                  ]}
                />
                <Para>These tools help avoid misunderstandings and support stable, respectful care relationships.</Para>

                <H3>Help understanding fair pay and costs</H3>
                <Bullets
                  accent="purple"
                  items={[
                    "Guidance on fair, transparent pay for carers",
                    "Clarity on what families are responsible for",
                    "Help avoid unexpected costs or assumptions",
                  ]}
                />
                <Para>This supports ethical care while still offering significant savings compared to agency fees.</Para>

                <H3>Support choosing and working with carers</H3>
                <Bullets
                  accent="purple"
                  items={[
                    "Understand what to look for in a carer",
                    "Ask the right questions before care starts",
                    "Build clear, professional working relationships",
                  ]}
                />
                <Para>This reduces stress for both families and carers.</Para>

                <H3>Clear agreements and peace of mind</H3>
                <Para>
                  When care becomes regular or more complex, written agreements matter. Paid members can access
                  structured agreement templates, guidance on when formal arrangements are needed and tools that support
                  clarity if circumstances change. These aren’t about paperwork — they’re about protecting everyone
                  involved.
                </Para>

                <H3>A calmer alternative to agency care</H3>
                <Para>Paid Family Membership supports families who want:</Para>
                <Bullets
                  accent="purple"
                  items={[
                    "More consistency in care",
                    "Direct communication with carers",
                    "Transparency and control",
                    "No long-term contracts or agency tie-ins",
                  ]}
                />
                <Para>You remain in control at every stage.</Para>

                <H3>What Paid Membership is not</H3>
                <Bullets
                  accent="purple"
                  items={[
                    "Is not an agency service",
                    "Does not place carers on your behalf",
                    "Does not remove your decision-making control",
                  ]}
                />
                <Para>It is a supportive structure, not a middleman.</Para>

                <H3>A simple way to think about it</H3>
                <Bullets
                  accent="purple"
                  items={[
                    "Free Family Membership → Learn, understand, plan",
                    "Paid Family Membership → Arrange care with clarity and confidence",
                  ]}
                />
                <Para>Both are valid. One simply offers more hands-on support when you’re ready.</Para>

                <H3>When to consider upgrading</H3>
                <Para>Families often upgrade when:</Para>
                <Bullets
                  accent="purple"
                  items={[
                    "They are ready to arrange care",
                    "They want clearer structure and agreements",
                    "They prefer guidance rather than guesswork",
                    "They want to avoid common care disputes",
                  ]}
                />
                <Para>There’s no deadline and no pressure.</Para>
                <div className="mt-5">
                  <a href="/membership/family-circle" className="btn-purple" data-testid="upgrade-coming-soon-inline">
                    Explore Family Care Office — £19/month
                  </a>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <WorkspaceSupportBlock />

      {/* Footer disclaimer */}
      <section data-testid="family-hub-disclaimer" className="rounded-3xl border border-slate-200 bg-[#f7f7f3] p-6 md:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
          <p className="text-xs md:text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Just Care Community is not a care agency,</strong> does not employ or manage
            carers, and does not provide regulated care services. Our resources are designed to support clearer, safer
            and more professional independent care arrangements.
          </p>
        </div>
      </section>
    </WorkspaceShell>
  );
}
